<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { api, messageFrom, setCsrfToken } from './api'
import LoginPage from './pages/LoginPage.vue'
import LibraryPage from './pages/LibraryPage.vue'
import PublicConversationPage from './pages/PublicConversationPage.vue'
import PublicSpeakersPage from './pages/PublicSpeakersPage.vue'
import QuestionsPage from './pages/QuestionsPage.vue'
import RecordingPage from './pages/RecordingPage.vue'
import SettingsPage from './pages/SettingsPage.vue'
import SpeakerPage from './pages/SpeakerPage.vue'
import SpeakersPage from './pages/SpeakersPage.vue'
import { getTelegramMiniApp, isTelegramMiniAppPath } from './telegram'

type Route =
  | { name: 'public-speakers' }
  | { name: 'public-conversation'; slug: string }
  | { name: 'admin-speakers' }
  | { name: 'admin-speaker'; id: string }
  | { name: 'admin-recording'; id: string }
  | { name: 'admin-settings' }
  | { name: 'admin-library' }
  | { name: 'admin-questions' }

const adminLoading = ref(false)
const adminChecked = ref(false)
const authenticated = ref(false)
const startupError = ref('')
const pathname = ref(window.location.pathname)
const telegramMode = isTelegramMiniAppPath()
const telegramMiniApp = getTelegramMiniApp()

const route = computed<Route>(() => {
  if (/^\/admin\/settings\/?$/i.test(pathname.value)) return { name: 'admin-settings' }
  if (/^\/admin\/library\/?$/i.test(pathname.value)) return { name: 'admin-library' }
  if (/^\/admin\/questions\/?$/i.test(pathname.value)) return { name: 'admin-questions' }
  const adminSpeaker = /^\/admin\/speakers\/([0-9a-f-]+)\/?$/i.exec(pathname.value)
  if (adminSpeaker?.[1]) return { name: 'admin-speaker', id: adminSpeaker[1] }
  const adminRecording = /^\/admin\/recordings\/([0-9a-f-]+)\/?$/i.exec(pathname.value)
  if (adminRecording?.[1]) return { name: 'admin-recording', id: adminRecording[1] }
  if (/^\/admin(?:\/)?$/i.test(pathname.value)) return { name: 'admin-speakers' }
  const telegramSpeaker = /^\/tg\/speakers\/([a-z0-9-]+)\/?$/i.exec(pathname.value)
  if (telegramSpeaker?.[1]) return { name: 'public-conversation', slug: telegramSpeaker[1] }
  const publicSpeaker = /^\/speakers\/([a-z0-9-]+)\/?$/i.exec(pathname.value)
  if (publicSpeaker?.[1]) return { name: 'public-conversation', slug: publicSpeaker[1] }
  return { name: 'public-speakers' }
})

const isAdminRoute = computed(() => route.value.name.startsWith('admin-'))

async function initializeAdmin(): Promise<void> {
  adminLoading.value = true
  startupError.value = ''
  try {
    const session = await api<{ authenticated: boolean; csrfToken: string }>('/api/admin/auth/session')
    setCsrfToken(session.csrfToken)
    authenticated.value = session.authenticated
    adminChecked.value = true
  } catch (error) {
    startupError.value = messageFrom(error)
  } finally {
    adminLoading.value = false
  }
}

function navigate(path: string): void {
  const target = telegramMode
    ? path === '/'
      ? '/tg'
      : path.startsWith('/speakers/')
        ? `/tg${path}`
        : path
    : path
  if (target !== window.location.pathname) window.history.pushState({}, '', target)
  pathname.value = target
  window.scrollTo({ top: 0, behavior: 'smooth' })
  if (target.startsWith('/admin') && !adminChecked.value && !adminLoading.value) void initializeAdmin()
}

function onPopState(): void {
  pathname.value = window.location.pathname
  if (isAdminRoute.value && !adminChecked.value && !adminLoading.value) void initializeAdmin()
}

function onUnauthorized(): void {
  authenticated.value = false
  adminChecked.value = true
  navigate('/admin')
}

async function logout(): Promise<void> {
  try {
    const session = await api<{ authenticated: boolean; csrfToken: string }>('/api/admin/auth/logout', {
      method: 'POST',
      body: '{}',
    })
    setCsrfToken(session.csrfToken)
  } finally {
    authenticated.value = false
    adminChecked.value = true
    navigate('/admin')
  }
}

watch(route, current => {
  telegramMiniApp?.setBackHandler(
    current.name === 'public-conversation' ? () => navigate('/') : null,
  )
}, { immediate: true })

onMounted(() => {
  window.addEventListener('popstate', onPopState)
  window.addEventListener('spikerskie:unauthorized', onUnauthorized)
  if (isAdminRoute.value) void initializeAdmin()
})
onBeforeUnmount(() => {
  window.removeEventListener('popstate', onPopState)
  window.removeEventListener('spikerskie:unauthorized', onUnauthorized)
  telegramMiniApp?.destroy()
})
</script>

<template>
  <PublicSpeakersPage v-if="route.name === 'public-speakers'" @navigate="navigate" />
  <PublicConversationPage
    v-else-if="route.name === 'public-conversation'"
    :key="route.slug"
    :slug="route.slug"
    @navigate="navigate"
  />

  <div v-else-if="adminLoading" class="boot-screen">Открываем админку…</div>
  <div v-else-if="startupError" class="boot-screen boot-screen--error">
    <p>{{ startupError }}</p>
    <button class="button button--primary" type="button" @click="initializeAdmin">Попробовать снова</button>
  </div>
  <LoginPage
    v-else-if="!authenticated"
    @logged-in="authenticated = true; adminChecked = true; navigate('/admin')"
  />
  <div v-else class="app-shell">
    <header class="site-header">
      <button class="brand" type="button" @click="navigate('/admin')">Спикерские</button>
      <div class="header-actions">
        <button class="logout-button" type="button" @click="navigate('/')">Публичная часть</button>
        <button class="logout-button" type="button" @click="navigate('/admin/questions')">Вопросы</button>
        <button class="logout-button" type="button" @click="navigate('/admin/library')">Литература</button>
        <button class="logout-button" type="button" @click="navigate('/admin/settings')">Настройки</button>
        <button class="logout-button" type="button" @click="logout">Выйти</button>
      </div>
    </header>
    <SpeakersPage v-if="route.name === 'admin-speakers'" @navigate="navigate" />
    <SpeakerPage
      v-else-if="route.name === 'admin-speaker'"
      :key="route.id"
      :id="route.id"
      @navigate="navigate"
    />
    <RecordingPage
      v-else-if="route.name === 'admin-recording'"
      :key="route.id"
      :id="route.id"
      @navigate="navigate"
    />
    <SettingsPage v-else-if="route.name === 'admin-settings'" @navigate="navigate" />
    <LibraryPage v-else-if="route.name === 'admin-library'" @navigate="navigate" />
    <QuestionsPage v-else @navigate="navigate" />
  </div>
</template>
